/**
 * The simulation.
 *
 * One fixed-step tick function over a world of vehicles moving along the road
 * graph. The same module runs on the server, which is authoritative, and on
 * the client, which runs it locally at M0 and will run it as prediction later.
 * There is no second implementation anywhere.
 *
 * A vehicle position is (edge, offset along that edge), never a free 2D point.
 * DESIGN.md section 5: that is the single biggest simplification available,
 * and it is what makes a camera read "traversed node N" rather than a
 * collision test.
 */

import {
  ACCELERATION,
  BRAKING,
  FREE_CORNER_SPEED,
  JUNCTION_RADIUS_METERS,
  LATERAL_ACCEL,
  MIN_CORNER_SPEED,
  TANK_GALLONS,
  FOLLOWING_METERS,
  LANE_WIDTH,
  PASS_LOOK_METERS,
  PASS_SECONDS,
  PASS_SIGHT_METERS,
  PASS_SLOWER_THAN,
  PASS_SPEED,
  VEHICLE_LENGTH,
  FUEL_MPG,
  METERS_PER_MILE,
  DAY_SECONDS,
  DWELL_SECONDS_MAX,
  DWELL_SECONDS_MIN,
  HABITS_MAX,
  HABITS_MIN,
  HABIT_RADIUS_METERS,
  UNPLANNED_TRIP_CHANCE,
  SPEED_MULTIPLIER_MAX,
  SPEED_MULTIPLIER_MIN,
  TICK_SECONDS,
} from './constants.ts';
import { edgeEnterableFrom, type County, otherNode } from './county.ts';
import { Router, largestComponent } from './graph.ts';
import { PlateRegistry, describeVehicle, type Description, type Plate } from './plates.ts';
import { Rng } from './rng.ts';

export type VehicleKind = 'civilian' | 'player' | 'patrol';

/**
 * Somewhere a driver goes regularly, and when.
 *
 * DESIGN.md section 8. This is the substrate the whole enforcement side rests
 * on: a vehicle that goes to the same few places on the same rhythm is a
 * vehicle whose next trip can be guessed and whose history has a shape. Without
 * it the traffic is busy and unreadable, there is nothing to reconstruct, and
 * the query interface returns noise no matter how well it is built.
 *
 * Nothing here is a job. It is the shop, the school run, the Sunday park, the
 * fuel stop that happens every third day because that is how long a tank lasts.
 */
export interface Habit {
  /** Where. */
  node: number;
  /** How often, in world seconds. */
  period: number;
  /**
   * The time this nominally happens at: the anchor the routine is hung on.
   *
   * Kept separately from `dueAt` so that the wander below does not accumulate.
   * An eight o'clock habit stays an eight o'clock habit however many mornings
   * somebody is late.
   */
  anchorAt: number;
  /** When it actually happens next. The anchor, plus a wander. */
  dueAt: number;
  /**
   * How far the time of day wanders, one standard deviation in world seconds.
   *
   * Nobody arrives anywhere at the same moment twice. Traffic, a phone call, a
   * bathroom stop. This is the single number that decides how predictable
   * somebody is, and the gradient it creates is the point: a person whose life
   * is tightly regular can be waited for, and a person whose is not cannot. How
   * exposed you are turns out to be a property of how regular you are, which is
   * a thing worth learning by playing rather than being told.
   */
  jitter: number;
  /** Chance of simply not going, this time round. */
  skipChance: number;
  /** How long they stay once there. */
  dwell: number;
}

export interface Vehicle {
  readonly id: number;
  readonly plate: Plate;
  /** What it looks like. Half of what a stop should have been checked against. */
  readonly description: Description;
  readonly kind: VehicleKind;

  /** Current edge, or -1 while parked at `atNode`. */
  edge: number;
  /** True when traveling from the edge A end toward the B end. */
  forward: boolean;
  /** Meters traveled along the current edge from the entry node. */
  offset: number;
  /** Node the vehicle is parked at, or -1 while moving. */
  atNode: number;
  /**
   * The road a parked car is standing on, or -1 if it has never driven.
   *
   * A parked vehicle lives at a graph *node*, and every node in this county is
   * a junction, so every parked car was sitting in the middle of an
   * intersection. Whole villages of them, stopped dead in crossroads.
   *
   * The graph position stays the node, because that is what routing and camera
   * reads and everything else are built on and none of it should bend for
   * scenery. This is the road it came in on, so it can be *drawn* pulled over
   * at the kerb a few meters short of the junction -- which is where a car that
   * arrived somewhere actually is. Nothing about the simulation changes: a read
   * happens on traversal, not on standing still.
   */
  parkedEdge: number;

  /**
   * Which lane, counted from the kerb: 0 is the right-hand lane.
   *
   * **-1 means the oncoming lane**, which is the only way past somebody on a
   * road with one lane each way, and is the reason this is a signed number
   * rather than an index. A car at -1 is doing something a car at 0 is not: it
   * is on the wrong side of the line, over the limit, and committed until it
   * gets back.
   *
   * The road decides the ceiling. `county.edgeLanes` carries lanes per
   * direction, out of the OpenStreetMap `lanes` tag, so a four-lane arterial
   * really has two each way and a section-line road really has one -- and the
   * difference between moving over and crossing over is a fact about that
   * road rather than a coin flip.
   */
  lane: number;
  /**
   * The one vehicle this driver is trying to reach, not follow.
   *
   * Set on a unit in pursuit. The following model keeps every car a braking
   * distance behind the car in front -- which is right for traffic and wrong
   * for a chase, where it parked the unit a polite ninety meters off the
   * runner's bumper forever. The quarry is exempted from the following
   * arithmetic; everything else on the road is still braked for.
   */
  quarry?: number;
  /**
   * Sim time at which an overtake is abandoned.
   *
   * Undefined when not passing. A driver who pulled out and could not get by
   * has to pull back in; without this they would ride the oncoming lane for
   * the rest of the trip, which is not a maneuver, it is a hazard with a
   * schedule.
   */
  passUntil?: number;

  /** Meters per second on the current edge. */
  speed: number;
  /** This driver relative to the posted speed. Some people speed. */
  /**
   * How this driver's speed relates to the posted limit.
   *
   * Not readonly any more. An administrator may run at up to twice the limit,
   * which is a real allowance rather than a cheat: enforcement drives to calls
   * and the game should not pretend the speed limit binds the people enforcing
   * it the way it binds everybody else. It is also, deliberately, visible --
   * a car doing 80 in a 40 is the most conspicuous thing on the road, and the
   * network is watching that car too.
   */
  speedMultiplier: number;
  /** What it was before any allowance was applied, so it can be handed back. */
  baseSpeedMultiplier?: number;
  /**
   * Litres in the tank.
   *
   * A leash, and the one mechanic that makes where you are constrain where you
   * can go. It also generates the thing section 8 says is the substrate: you
   * fuel up when the tank gets low, which is a schedule whether you think of it
   * as one or not, and it puts you at the same two or three real filling
   * stations near the roads you actually drive.
   */
  fuel: number;

  /** Edges still to traverse, `routeIndex` being the one in progress. */
  route: number[];
  routeIndex: number;
  destination: number;

  /** Sim seconds at which a parked vehicle sets off again. */
  dwellUntil: number;

  /** Where this vehicle sleeps, and where it returns to between errands. */
  home: number;
  /** The places it goes back to, and when. */
  habits: Habit[];
  /** World time of the next trip that is not on the schedule. */
  whimAt: number;

  /**
   * Whether this vehicle picks its own destinations.
   *
   * True for simulated traffic, which is the whole county deciding where to go
   * on its own. False for a player, who is parked until they say otherwise:
   * DESIGN.md section 5, clients send intents, so a player vehicle that has
   * not been given one must sit there rather than wander off.
   */
  autonomous: boolean;
  /**
   * Being driven by hand rather than following a route.
   *
   * The car still lives on the graph as (edge, offset) -- §5's model does not
   * bend for this and should not, because free 2D movement is what makes a
   * plate reader impossible to simulate honestly. Manual driving is therefore
   * not a second movement system: it is the same route follower with the route
   * extended one edge at a time, by whoever is steering, at each junction.
   *
   * Which is also why it feels like driving rather than like nudging a dot.
   * Everything the autonomous cars get -- acceleration, braking into corners,
   * cornering speed by radius -- applies unchanged, because it is the same
   * code path.
   */
  manual?: boolean;
  /** 0 to 1, scaling the posted limit. What the accelerator is doing. */
  throttle?: number;
  /**
   * The compass direction the driver wants to be going, in radians, or
   * undefined for "carry straight on".
   *
   * **A heading, not a turn.** W is north and S is south, which is what those
   * keys mean everywhere -- and it is the right model for this county besides:
   * Waukesha is a section grid, so "keep going north" is a thing a driver
   * actually thinks and "take the second left" is not.
   *
   * The road does the rest. Roads do not run along the compass -- a county road
   * bends thirty degrees between junctions -- so at each junction the car takes
   * whichever road leaves closest to the direction asked for. That is the
   * auto-tracking: a driver names a direction and the network supplies the
   * geometry, which is also how anybody drives somewhere they roughly know.
   *
   * It stands until changed. Holding a key to cross a county would be a message
   * a frame for a decision that changes once a junction.
   */
  want?: number | undefined;
  /**
   * World seconds this unit stays on a call.
   *
   * Only ever set on a patrol. It drives no differently -- this is not a
   * pursuit model -- it simply has its lights on, which is the visible half of
   * an alert and the only part a passer-by ever sees.
   */
  respondingUntil?: number;
  /** What this unit is out on, for anybody with a scanner. */
  call?: string;
  /** The plate it concerns, when it concerns one. */
  callPlate?: string;
  /**
   * A county sheriff's deputy rather than a municipal officer.
   *
   * Set on a patrol at spawn. A deputy's jurisdiction is the whole county, so
   * it answers a hotlist call anywhere without being invited as backup, and its
   * revenue books to the sheriff's office. A municipal unit answers only on its
   * own turf.
   */
  sheriff?: boolean;

  /** World position after the last tick, and the tick before it. The client
   *  renders between the two so movement is smooth above the tick rate. */
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  heading: number;
  prevHeading: number;
}

/**
 * Fired when a vehicle passes through a node. Every plate reader in the game
 * hangs off this one event, which is the whole reason positions are graph
 * positions.
 */
export interface NodeTraversal {
  vehicle: Vehicle;
  node: number;
  /** Edge arrived on. */
  fromEdge: number;
  /** Edge departed on, or -1 when the trip ended here. */
  toEdge: number;
  /** Sim seconds. */
  time: number;
}

export type TraversalListener = (event: NodeTraversal) => void;

export interface World {
  readonly county: County;
  /** How long a day is here. See DAY_SECONDS. */
  readonly daySeconds: number;
  readonly rng: Rng;
  readonly router: Router;
  readonly plates: PlateRegistry;
  /** Nodes in the largest connected component: everywhere reachable. */
  readonly reachable: Int32Array;
  /**
   * Running total of destination weight over `reachable`, for sampling.
   *
   * Built on first use. A client running no simulated traffic never needs it,
   * and building it costs a pass over every node for every settlement, which
   * on a real county is a few million exponentials for nothing.
   */
  destinationCdf: Float64Array | null;

  /** Integer ticks since the world started. */
  tick: number;
  /** Sim seconds since the world started. */
  time: number;

  vehicles: Vehicle[];
  readonly traversalListeners: TraversalListener[];

  /** Trips that could not be routed on the last tick. Nonzero means the graph
   *  has a problem worth looking at, so it is surfaced rather than swallowed. */
  unroutableTrips: number;
  /** Vehicles by (edge, direction), rebuilt each tick so cars can see ahead. */
  traffic?: Map<number, Vehicle[]>;
}

export interface WorldOptions {
  seed?: number;
  civilianCount?: number;
  /** Marked units on the road. A county with police in it should show some. */
  patrolCount?: number;
  /**
   * Share of vehicles that begin mid-trip rather than parked.
   *
   * Without this a fresh world is a county full of stationary cars for the
   * first several minutes, because every vehicle starts at a node and waits
   * out a dwell. Traffic is the noise the whole game runs on, so it has to be
   * there from the first tick.
   */
  warmStartShare?: number;
  /** Length of a day in world seconds. Defaults to DAY_SECONDS. */
  daySeconds?: number;
  /**
   * World time to start the clock at.
   *
   * A restarted server that began again at zero would replay the same
   * deterministic world into a table that never forgets, writing the same reads
   * a second time with the same timestamps. The county is persistent and its
   * record is the whole point, so the clock resumes from whatever the record
   * already holds.
   */
  startTime?: number;
  /**
   * Plates already spoken for, held before any civilian is generated.
   *
   * A county regenerates deterministically on every boot, but the players who
   * turn up afterward do not, so a plate issued to a player last week is free
   * again this morning and a simulated car will eventually be handed it. Then
   * two vehicles wear one plate and every query about it returns two lives --
   * which is a real failure this game models on purpose elsewhere, and must
   * never be something the server does to a player by accident.
   */
  heldPlates?: readonly string[];
}

export function createWorld(county: County, options: WorldOptions = {}): World {
  const seed = options.seed ?? 1;
  const rng = new Rng(seed);
  const reachable = largestComponent(county);
  const world: World = {
    county,
    daySeconds: options.daySeconds ?? DAY_SECONDS,
    rng,
    router: new Router(county),
    plates: new PlateRegistry(county.state),
    reachable,
    destinationCdf: null,
    tick: 0,
    time: options.startTime ?? 0,
    vehicles: [],
    traversalListeners: [],
    unroutableTrips: 0,
  };

  for (const plate of options.heldPlates ?? []) world.plates.hold(plate);

  const civilians = options.civilianCount ?? 0;
  const warmShare = options.warmStartShare ?? 0.7;
  for (let i = 0; i < civilians; i++) {
    const vehicle = spawnVehicle(world, 'civilian');
    if (rng.chance(warmShare)) warmStart(world, vehicle);
  }

  /**
   * Marked units on the road.
   *
   * There were none, which left a county with twenty-one police departments in
   * it and not one squad car anywhere -- and the enforcement half of this game
   * is *about* those departments. A player driving around should be able to
   * see that they exist, and see one behind them, without a word being said.
   *
   * They drive like everybody else. Nothing in this simulation gives them
   * powers a civilian does not have, because §8's whole argument is that the
   * power is the network rather than the car.
   */
  const patrols = options.patrolCount ?? 0;
  for (let i = 0; i < patrols; i++) {
    const vehicle = spawnVehicle(world, 'patrol');
    if (rng.chance(warmShare)) warmStart(world, vehicle);
  }

  return world;
}

let nextVehicleId = 1;

/** Reset the vehicle id counter. Only for tests that assert on ids. */
export function resetVehicleIds(): void {
  nextVehicleId = 1;
}

export function spawnVehicle(
  world: World,
  kind: VehicleKind,
  homeState?: boolean,
  atNode?: number,
): Vehicle {
  const autonomous = kind === 'civilian' || kind === 'patrol';
  const { rng } = world;
  // Vehicles start where people are, for the same reason they drive there --
  // unless a home node is given, which is how a returning player is put back in
  // their own garage rather than somewhere new every time.
  const node =
    atNode !== undefined && atNode >= 0 && atNode < world.county.nodeCount
      ? atNode
      : pickDestination(world);
  // Issued first, because the description is a function of the plate: a
  // registration entry describes the vehicle a plate was issued to, and rolling
  // the two independently made every hotlist entry disagree with the car it was
  // filed about. See describeVehicle.
  // A marked unit carries a municipal plate, which is a fact about the car
  // anybody can read off the back of it without a database.
  const plate =
    kind === 'patrol' ? world.plates.issueMunicipal(rng) : world.plates.issue(rng, homeState);
  const vehicle: Vehicle = {
    id: nextVehicleId++,
    plate,
    description: describeVehicle(`${plate.abbr}:${plate.text}`),
    // Somewhere between a third and full, so the county is not a fleet of cars
    // that all need filling on the same afternoon.
    fuel: TANK_GALLONS * rng.range(0.35, 1),
    kind,
    edge: -1,
    forward: true,
    offset: 0,
    atNode: node,
    // A car that has never driven anywhere is still standing somewhere, and
    // "somewhere" cannot be the middle of a junction. Any road off this node
    // will do -- it has no history to be faithful to.
    parkedEdge: anyRoadAt(world.county, node),
    lane: 0,
    speed: 0,
    speedMultiplier: rng.range(SPEED_MULTIPLIER_MIN, SPEED_MULTIPLIER_MAX),
    route: [],
    routeIndex: 0,
    destination: node,
    // Stagger the first departure so a fresh world does not launch every
    // vehicle on the same tick.
    dwellUntil: rng.range(0, DWELL_SECONDS_MAX / 2),
    autonomous,
    home: node,
    habits: [],
    whimAt: world.time + rng.range(0.008, 0.03) * world.daySeconds,
    x: world.county.nodeX[node] as number,
    y: world.county.nodeY[node] as number,
    prevX: world.county.nodeX[node] as number,
    prevY: world.county.nodeY[node] as number,
    heading: 0,
    prevHeading: 0,
  };
  if (autonomous) vehicle.habits = buildHabits(world, vehicle.home);
  world.vehicles.push(vehicle);
  return vehicle;
}

/**
 * The handful of places this driver keeps going back to.
 *
 * Periods are in days rather than hours because that is how a routine is
 * actually shaped: work is every day, the shop is every couple of days, the
 * thing at the park is once a week. The phase is a time of day, so that
 * everybody's work trips land at roughly the same hour and rush hours fall out
 * of the schedule rather than being drawn in.
 */
function buildHabits(world: World, home: number): Habit[] {
  const { county, rng } = world;
  const day = world.daySeconds;
  const homeX = county.nodeX[home] as number;
  const homeY = county.nodeY[home] as number;
  const radiusSquared = HABIT_RADIUS_METERS * HABIT_RADIUS_METERS;

  const count = HABITS_MIN + rng.int(HABITS_MAX - HABITS_MIN + 1);
  const habits: Habit[] = [];

  for (let i = 0; i < count; i++) {
    // Somewhere people go, but near home. A regular place on the far side of
    // the county is not a routine, it is a coincidence, and a week made of
    // those has no shape for anybody to find.
    let node = -1;
    for (let attempt = 0; attempt < 24; attempt++) {
      const candidate = pickDestination(world);
      if (candidate === home) continue;
      const dx = (county.nodeX[candidate] as number) - homeX;
      const dy = (county.nodeY[candidate] as number) - homeY;
      if (dx * dx + dy * dy > radiusSquared) continue;
      node = candidate;
      break;
    }
    if (node < 0) continue;

    // The first one is the daily trip that most people have. The rest are
    // spread out, and the more often somewhere is visited the less time is
    // spent there.
    const days = i === 0 ? 1 : 1 + rng.int(7);
    const period = days * day;
    // Times of day, and people are asleep for some of them. Spreading errands
    // evenly around the clock leaves the county as busy at four in the morning
    // as at noon, and a night with no traffic in it is worth having: it is when
    // being on the road is conspicuous.
    const phase = i === 0 ? rng.range(0.3, 0.42) : rng.range(0.28, 0.88);
    const dwell = days === 1 ? rng.range(0.04, 0.12) * day : rng.range(0.01, 0.04) * day;

    // The daily trip is the one somebody has to be somewhere for, so it is the
    // tight one: about a quarter hour either side, and rarely skipped. The rest
    // are errands, which happen when they happen and get skipped when the week
    // gets away from somebody.
    const jitter = (days === 1 ? rng.range(0.006, 0.014) : rng.range(0.02, 0.07)) * day;
    const skipChance = days === 1 ? rng.range(0.02, 0.06) : rng.range(0.08, 0.2);

    // Somewhere inside its own cycle, so the world does not begin with every
    // errand in the county due at once, and anchored to a real time of day even
    // when the clock resumed mid-history rather than at zero.
    const dayStart = Math.floor(world.time / day) * day;
    let anchorAt = dayStart + (phase + rng.int(days)) * day;
    while (anchorAt <= world.time) anchorAt += period;

    habits.push({
      node,
      period,
      anchorAt,
      dueAt: anchorAt + rng.normal(0, jitter),
      jitter,
      skipChance,
      dwell,
    });
  }

  return habits;
}

/**
 * Where this driver is going next, or -1 when they have no reason to move.
 *
 * A habit that has come due wins. Otherwise they go home if they are not
 * already there, and if they are, they mostly stay put. That last part matters
 * as much as the rest: a county where everybody is always driving has no rhythm
 * in it, and the empty hours are what make the busy ones legible.
 */
function nextDestination(world: World, vehicle: Vehicle): number {
  const now = world.time;

  let due: Habit | null = null;
  for (const habit of vehicle.habits) {
    if (habit.dueAt > now) continue;
    if (!due || habit.dueAt < due.dueAt) due = habit;
  }

  if (due) {
    rollForward(world, due, now);
    return due.node;
  }

  if (vehicle.atNode !== vehicle.home) return vehicle.home;

  // Something unplanned, on its own rate rather than a chance rolled every time
  // the vehicle is looked at. Rolling per check made an idle car take a
  // county-wide trip within minutes, every time, which is how half the county
  // ended up permanently in motion.
  if (world.time >= vehicle.whimAt) {
    vehicle.whimAt = world.time + world.rng.range(0.008, 0.03) * world.daySeconds;
    if (world.rng.chance(UNPLANNED_TRIP_CHANCE)) return pickDestination(world);
  }
  return -1;
}

/**
 * Schedule the next occurrence of a habit.
 *
 * The anchor moves by whole periods, so the routine stays pinned to its time of
 * day. What wanders is the occurrence, and it wanders around the anchor rather
 * than away from it. A skipped visit costs a whole period, which is what a
 * missed week looks like from the outside: not a shifted routine, a gap in one.
 *
 * The loop also rolls past any occurrences that have already gone by, so a
 * vehicle parked for a long stretch does not then run every errand it missed
 * back to back.
 */
function rollForward(world: World, habit: Habit, now: number): void {
  const rng = world.rng;
  for (let guard = 0; guard < 512; guard++) {
    habit.anchorAt += habit.period;
    if (rng.chance(habit.skipChance)) habit.anchorAt += habit.period;
    habit.dueAt = habit.anchorAt + rng.normal(0, habit.jitter);
    if (habit.dueAt > now) return;
  }
  // Pathological only: a jitter large enough to keep landing in the past.
  habit.dueAt = now + habit.period;
}

/** When the next thing on this driver's schedule comes round. */
function nextDueAt(world: World, vehicle: Vehicle): number {
  let soonest = vehicle.whimAt;
  for (const habit of vehicle.habits) soonest = Math.min(soonest, habit.dueAt);
  // No habits and no whim: look again eventually rather than never.
  return Number.isFinite(soonest) ? soonest : world.time + DWELL_SECONDS_MAX;
}

/**
 * Drop a vehicle somewhere along a trip already in progress.
 *
 * Only used at world creation. Everything after that arrives at a position by
 * having driven there, which is the property the detection table depends on.
 */
export function warmStart(world: World, vehicle: Vehicle): void {
  const origin = vehicle.atNode;
  if (origin < 0) return;
  // Deliberately not the schedule. At world time zero nothing is due yet, and
  // the pretence being maintained is that the county has been running for a
  // while, so these cars are already out on an errand.
  if (!beginTripTo(world, vehicle, pickDestination(world))) return;

  const county = world.county;
  const index = world.rng.int(vehicle.route.length);

  let node = origin;
  for (let i = 0; i < index; i++) {
    node = otherNode(county, vehicle.route[i] as number, node);
  }

  const edge = vehicle.route[index] as number;
  enterEdge(world, vehicle, edge, node);
  vehicle.routeIndex = index;
  vehicle.offset = world.rng.next() * (county.edgeLen[edge] as number);
  updatePosition(world, vehicle);
  vehicle.prevX = vehicle.x;
  vehicle.prevY = vehicle.y;
  vehicle.prevHeading = vehicle.heading;
}

/**
 * Advance the world by exactly one tick. Callers drive this from a fixed-step
 * accumulator; nothing here reads a wall clock.
 */
export function tickWorld(world: World, dt: number = TICK_SECONDS): void {
  world.tick++;
  world.time += dt;
  world.unroutableTrips = 0;

  buildTraffic(world);

  for (const vehicle of world.vehicles) {
    vehicle.prevX = vehicle.x;
    vehicle.prevY = vehicle.y;
    vehicle.prevHeading = vehicle.heading;
    advanceVehicle(world, vehicle, dt);
  }
}

/**
 * Who is on which approach, so cars can see the one in front.
 *
 * There was no such thing, and it showed: a county's worth of traffic drove
 * straight through itself and piled up in the same spot at junctions, because
 * nothing in the mover had any idea another vehicle existed. Cars stacked
 * exactly on top of each other and separated again, which is the single most
 * visible way a simulation stops looking like traffic.
 *
 * Keyed on `(edge, direction, lane)` rather than on edge. The two sides of a
 * road are two queues -- a car going the other way is oncoming, not in front --
 * and on a four-lane arterial the two lanes are two more, because a car in the
 * left lane is not something the car in the right lane has to brake for. That
 * is the whole of what makes overtaking work: pulling out is joining a
 * different queue, and the queue you joined is usually empty.
 *
 * Sorted by offset so the one ahead is the next entry along.
 *
 * Rebuilt every tick from scratch. That is O(n) on a county of 2,200 vehicles
 * and it beats maintaining an incremental index that has to be right through
 * every junction crossing, park, respawn and teleport in the game.
 */
function buildTraffic(world: World): void {
  const lanes = (world.traffic ??= new Map<number, Vehicle[]>());
  for (const queue of lanes.values()) queue.length = 0;

  for (const vehicle of world.vehicles) {
    if (vehicle.edge < 0) continue;
    const key = laneKey(vehicle.edge, vehicle.forward, vehicle.lane);
    const queue = lanes.get(key);
    if (queue) queue.push(vehicle);
    else lanes.set(key, [vehicle]);
  }

  for (const queue of lanes.values()) {
    if (queue.length > 1) queue.sort((a, b) => a.offset - b.offset);
  }
}

/**
 * How far there is to the back of the car in front, on this approach.
 *
 * Infinity when the road ahead is clear. Only looks at the same lane, so a car
 * coming the other way is not something to brake for -- which is right, and is
 * also why oncoming traffic passes rather than deadlocking.
 */
/**
 * One queue per (edge, direction, lane).
 *
 * Lane -1 is the oncoming lane, so the stored index is shifted by one to keep
 * the key a non-negative integer. Six lane slots is more than
 * `osm.MAX_LANES` needs and leaves the arithmetic obviously safe.
 */
function laneKey(edge: number, forward: boolean, lane: number): number {
  return (edge * 2 + (forward ? 1 : 0)) * 6 + (lane + 1);
}

function gapAhead(world: World, vehicle: Vehicle, lane = vehicle.lane): number {
  const queue = world.traffic?.get(laneKey(vehicle.edge, vehicle.forward, lane));
  if (!queue || queue.length === 0) return Number.POSITIVE_INFINITY;

  let best = Number.POSITIVE_INFINITY;
  for (const other of queue) {
    if (other === vehicle) continue;
    // The exemption runs both ways. The car being chased is a destination, not
    // traffic (skip it as the pursuer). And a car never brakes for the unit
    // chasing it: being wedged to a stop behind your own pursuer is a trap, not
    // traffic, and it is exactly what happened when a pursuit unit parked on the
    // junction a fleeing player was arriving at. See Vehicle.quarry.
    if (other.id === vehicle.quarry || other.quarry === vehicle.id) continue;
    const ahead = other.offset - vehicle.offset;
    // Only what is in front, and only the nearest of it.
    if (ahead > 0 && ahead < best) best = ahead;
  }
  return best === Number.POSITIVE_INFINITY ? best : best - VEHICLE_LENGTH;
}

/**
 * The vehicle immediately in front in a given lane, or null.
 *
 * `gapAhead` answers how far; this answers who, which is the question the
 * overtaking rule actually asks. Nobody pulls out to pass a car that is doing
 * the limit -- they pull out to pass a car that is not -- and that is a fact
 * about the other driver rather than about the distance to them.
 */
function leadVehicle(world: World, vehicle: Vehicle, lane = vehicle.lane): Vehicle | null {
  const queue = world.traffic?.get(laneKey(vehicle.edge, vehicle.forward, lane));
  if (!queue) return null;

  let best: Vehicle | null = null;
  let bestAhead = Number.POSITIVE_INFINITY;
  for (const other of queue) {
    if (other === vehicle) continue;
    const ahead = other.offset - vehicle.offset;
    if (ahead > 0 && ahead < bestAhead) {
      bestAhead = ahead;
      best = other;
    }
  }
  return best;
}

/**
 * Whether anybody is coming the other way inside `meters`.
 *
 * The other direction measures its offsets from the other end of the edge, so
 * an oncoming car at offset `o` is at `length - o` in this driver's frame. Get
 * that backwards and an overtake is cleared exactly when it should be refused,
 * which is the worst possible way to be wrong here.
 *
 * Both oncoming lanes are checked, because a car already out passing somebody
 * the other way is closing at twice the speed and is not something to plan
 * around -- it is a reason nobody should be out there.
 *
 * **A short clearance behind counts too**, which is not obvious and was the one
 * thing measurement caught first. Refusing only what is ahead let a driver pull
 * out the instant an oncoming car drew level: the two were 1.6 m apart at the
 * closest, separating rather than closing, so it was never a head-on -- it was
 * a sideswipe. A car you have only just passed is still beside you.
 *
 * **And it looks past the junction**, which is the second thing measurement
 * caught and the more serious of the two. Checking only this edge missed every
 * car that had not turned onto it yet: one trace showed a driver out passing on
 * a 158 m road, thirty ticks with the road ahead genuinely empty, and then a
 * car turning in at the far end 34 m away with no warning at all. The road
 * ahead of you does not end at the junction, so neither does this.
 */
function oncomingWithin(world: World, vehicle: Vehicle, meters: number): boolean {
  const county = world.county;
  const length = county.edgeLen[vehicle.edge] as number;

  for (const lane of [0, -1]) {
    const queue = world.traffic?.get(laneKey(vehicle.edge, !vehicle.forward, lane));
    if (!queue) continue;
    for (const other of queue) {
      const ahead = length - other.offset - vehicle.offset;
      if (ahead <= meters && ahead >= -OVERTAKE_CLEARANCE) return true;
    }
  }

  // Past the junction ahead: anybody on a road that feeds it, heading for it.
  const toJunction = length - vehicle.offset;
  if (toJunction >= meters) return false;

  const node = vehicle.forward
    ? (county.edgeB[vehicle.edge] as number)
    : (county.edgeA[vehicle.edge] as number);
  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;

  for (let slot = start; slot < end; slot++) {
    const feeder = county.adjEdge[slot] as number;
    if (feeder === vehicle.edge) continue;
    const feederLength = county.edgeLen[feeder] as number;
    // A vehicle heads for this node when the node is the end it is traveling
    // toward, which is B if it is going forward and A if it is not.
    const towardForward = (county.edgeB[feeder] as number) === node;

    for (const lane of [0, -1]) {
      const queue = world.traffic?.get(laneKey(feeder, towardForward, lane));
      if (!queue) continue;
      for (const other of queue) {
        if (feederLength - other.offset + toJunction <= meters) return true;
      }
    }
  }
  return false;
}

/** How far behind an oncoming car has to be before pulling out is not a sideswipe. */
const OVERTAKE_CLEARANCE = VEHICLE_LENGTH * 3;

/**
 * Decide which lane this vehicle should be in, and whether it is overtaking.
 *
 * Two different maneuvers wearing the same name, and the road says which one
 * is available.
 *
 * **Where there is more than one lane each way**, going round somebody is
 * moving over. Nothing is crossed, nothing is risked, and there is no offence
 * in it -- so a car held up by a slower one drifts left, and drifts back right
 * when the road ahead is clear. That is the behavior that makes a four-lane
 * arterial read as a four-lane arterial rather than as two single files.
 *
 * **Where there is one lane each way**, going round somebody means using the
 * oncoming lane. That is a real decision with real conditions on it: the car in
 * front has to actually be slow, the other side has to be clear for a quarter
 * mile, and the driver has to be willing to exceed the limit to get by --
 * because you cannot pass anybody at the speed they are doing. All three are
 * required. Failing any one of them, the car sits behind and waits, which is
 * what most drivers do most of the time.
 *
 * A player is held to the same rules and the same trigger: full throttle behind
 * somebody slow. That is not the game deciding for them -- it is what pressing
 * the accelerator to the floor behind a slow car *means*, and it keeps one code
 * path for a maneuver whose whole point is that it is visible to anybody
 * watching.
 */
function chooseLane(world: World, vehicle: Vehicle, limit: number): void {
  const county = world.county;
  const lanes = Math.max(1, county.edgeLanes[vehicle.edge] as number);

  // Already out passing somebody: the only question is when to come back in.
  if (vehicle.lane < 0) {
    const done =
      world.time >= (vehicle.passUntil ?? 0) ||
      oncomingWithin(world, vehicle, PASS_SIGHT_METERS / 2) ||
      gapAhead(world, vehicle, 0) > PASS_LOOK_METERS;
    if (done) {
      vehicle.lane = 0;
      delete vehicle.passUntil;
    }
    return;
  }

  if (vehicle.lane >= lanes) vehicle.lane = lanes - 1;

  /**
   * Held up: somebody close in front, slower than this driver wants to go.
   *
   * Measured against *this* driver's limit, because being held up is a fact
   * about what you wanted. Crossing the center line has a second and stricter
   * condition below, measured against the posted limit instead -- which is the
   * distinction the first version of this got wrong. A driver willing to do
   * 20% over treated the limit itself as slow, so on a road where everybody was
   * doing exactly what they should, the speeders passed everybody.
   */
  const lead = leadVehicle(world, vehicle);
  const blocked =
    lead !== null &&
    lead.offset - vehicle.offset <= PASS_LOOK_METERS &&
    lead.speed < limit * PASS_SLOWER_THAN;

  if (lanes > 1) {
    if (blocked && vehicle.lane + 1 < lanes) {
      // Only if the lane being moved into is actually better.
      if (gapAhead(world, vehicle, vehicle.lane + 1) > PASS_LOOK_METERS) vehicle.lane++;
      return;
    }
    // Keep right except to pass, which is both the law and the reason the
    // outside lane stays available for the next car that needs it.
    if (!blocked && vehicle.lane > 0 && gapAhead(world, vehicle, vehicle.lane - 1) > PASS_LOOK_METERS) {
      vehicle.lane--;
    }
    return;
  }

  // One lane each way. Crossing the line, or nothing.
  if (!blocked || lead === null) return;
  // Nobody crosses the center line to get past a car doing the limit. This is
  // the posted limit rather than the driver's own, so a person willing to speed
  // does not get to call everybody else slow.
  const posted = county.edgeSpeed[vehicle.edge] as number;
  if (lead.speed >= posted * PASS_SLOWER_THAN) return;
  if ((county.edgeOneway[vehicle.edge] as number) !== 0) return;
  // A driver has to want it. For traffic that is a willingness to speed, which
  // most of it has; for a player it is the accelerator on the floor.
  if (vehicle.manual === true ? (vehicle.throttle ?? 0) < 0.95 : vehicle.speedMultiplier < 1) {
    return;
  }
  // Room to get back in, and nothing coming.
  const length = county.edgeLen[vehicle.edge] as number;
  if (length - vehicle.offset < PASS_LOOK_METERS) return;
  if (oncomingWithin(world, vehicle, PASS_SIGHT_METERS)) return;

  vehicle.lane = -1;
  vehicle.passUntil = world.time + PASS_SECONDS;
}

/**
 * Advance one vehicle by `dt`.
 *
 * Exported because the client predicts its own vehicle with it, running the
 * same code the server runs rather than an approximation of it. That is the
 * whole reason the simulation is a shared module.
 */
export function stepVehicle(world: World, vehicle: Vehicle, dt: number = TICK_SECONDS): void {
  vehicle.prevX = vehicle.x;
  vehicle.prevY = vehicle.y;
  vehicle.prevHeading = vehicle.heading;
  advanceVehicle(world, vehicle, dt);
}

function advanceVehicle(world: World, vehicle: Vehicle, dt: number): void {
  if (vehicle.edge < 0) {
    /**
     * Pulling away from a standstill.
     *
     * A parked car is at a junction with no edge under it, and everything below
     * this assumes there is one. Route following gets onto a road by being
     * given a route; a driver gets onto one by pressing a key, and without this
     * pressing a key did nothing at all -- the throttle went up and the car sat
     * there, because nothing had put it on a road to move along.
     */
    if (vehicle.manual && (vehicle.throttle ?? 0) > 0 && vehicle.atNode >= 0) {
      const out = pickExit(world, vehicle, vehicle.atNode, -1, vehicle.want);
      if (out >= 0) {
        const from = vehicle.atNode;
        vehicle.route = [out];
        vehicle.routeIndex = 0;
        enterEdge(world, vehicle, out, from);
        vehicle.dwellUntil = 0;
      }
    }
    if (vehicle.edge < 0) {
      // A player waits to be told. Only simulated traffic invents a destination.
      const waiting = !vehicle.autonomous || world.time < vehicle.dwellUntil;
      if (waiting || !startTrip(world, vehicle)) {
      if (!waiting) {
        // Either nothing on the schedule is due, or nothing could be routed.
        // Sleeping until the next errand is what keeps a parked county quiet
        // instead of re-planning every car every tick.
        // Sleep right through to the next thing, with no cap. Waking every few
        // minutes to re-ask was the other half of why the county never stood
        // still.
        vehicle.dwellUntil = nextDueAt(world, vehicle);
        if (vehicle.dwellUntil <= world.time) {
          world.unroutableTrips++;
          vehicle.dwellUntil = world.time + 5;
        }
      }
      // Still parked, but its world position must follow the node it is parked
      // at. Returning without this left a stationary vehicle drawn wherever it
      // last happened to be, which on a client whose vehicle was moved by the
      // server is a car sitting in the wrong town.
      updatePosition(world, vehicle);
      return;
      }
    }
  }

  const county = world.county;

  // Accelerate toward the posted limit, and brake for whatever is coming.
  //
  // Without this every car takes every corner at the limit and a junction is
  // the fastest part of a trip, which is backwards -- and it matters beyond
  // looking right, because the speed a car is doing at a camera decides both
  // how likely the read is wrong and how likely the driver notices the camera.
  // Cameras sit on junction approaches at a median setback of 44 m, which is
  // exactly where a turning car is slowing down.
  const limit = (county.edgeSpeed[vehicle.edge] as number) * vehicle.speedMultiplier;
  const nextEdge = vehicle.route[vehicle.routeIndex + 1] ?? -1;
  const node = vehicle.forward
    ? (county.edgeB[vehicle.edge] as number)
    : (county.edgeA[vehicle.edge] as number);

  // How slow this car has to be by the time it reaches the junction ahead.
  // A route end (nextEdge < 0) normally means "park here," so the car brakes to
  // a stop -- except a unit in pursuit, which is re-aimed every second and must
  // not decelerate to park on the junction its quarry is arriving at. It keeps
  // chase speed through the node and crossNode carries it straight on.
  const ahead =
    nextEdge >= 0
      ? cornerSpeed(county, vehicle.edge, nextEdge, node)
      : vehicle.quarry !== undefined
        ? limit
        : 0;
  const toGo = (county.edgeLen[vehicle.edge] as number) - vehicle.offset;

  // Which lane, and whether this car is on the wrong side of the line getting
  // round somebody. Decided before the speed, because it changes what the
  // speed should be and which car is in front.
  chooseLane(world, vehicle, limit);

  // What the accelerator is asking for. Autonomous traffic always asks for
  // the limit; a driver asks for whatever they are asking for.
  //
  // A car out passing asks for more than the limit, because it has to: you
  // cannot get by somebody at the speed they are doing. That is exactly what
  // makes an overtake visible -- over the line and over the limit, both at
  // once, in front of anybody who happens to be watching.
  let target = limit * (vehicle.throttle ?? 1);
  if (vehicle.lane < 0) target = Math.max(target, limit * PASS_SPEED);

  /**
   * The car in front.
   *
   * Same braking arithmetic as a corner: what speed can be held here and still
   * be stopped by the time the gap runs out. A following distance is added on
   * top so traffic sits a car's length apart at rest rather than bumper to
   * bumper, which is both what happens and what makes a queue readable on a
   * map.
   *
   * This is the whole of the collision model, and it is enough: vehicles live
   * on `(edge, offset)` and cannot be beside each other, so "do not pass the
   * one in front" is the only rule the geometry can break.
   */
  const gap = gapAhead(world, vehicle) - FOLLOWING_METERS;
  if (Number.isFinite(gap)) {
    const canDo = gap <= 0 ? 0 : Math.sqrt(2 * BRAKING * gap);
    target = Math.min(target, canDo);
  }

  if (Number.isFinite(ahead) && ahead < vehicle.speed) {
    // v^2 = u^2 + 2as, solved for the speed it is safe to be doing here.
    const canDo = Math.sqrt(Math.max(0, ahead * ahead + 2 * BRAKING * Math.max(0, toGo)));
    target = Math.min(target, canDo);
  }

  if (vehicle.speed < target) {
    vehicle.speed = Math.min(target, vehicle.speed + ACCELERATION * dt);
  } else if (vehicle.speed > target) {
    vehicle.speed = Math.max(target, vehicle.speed - BRAKING * dt);
  }
  /**
   * A floor, but only for a car that is trying to move.
   *
   * This exists so simulated traffic never grinds to a halt creeping round a
   * tight corner -- at zero it would never get out of it. Applied
   * unconditionally it also meant a car could not *stop*: a driver lifting off
   * the accelerator coasted down to exactly MIN_CORNER_SPEED and stayed there
   * for good, which is the sort of thing that only shows up the first time
   * somebody tries to park.
   */
  // ...unless there is something in the way, in which case stopping is exactly
  // what is wanted and the corner floor would drive straight into it.
  if (target > 0 && Number.isFinite(gap) && gap > MIN_CORNER_SPEED) {
    vehicle.speed = Math.max(vehicle.speed, MIN_CORNER_SPEED);
  } else if (target > 0 && !Number.isFinite(gap)) {
    vehicle.speed = Math.max(vehicle.speed, MIN_CORNER_SPEED);
  }

  // Burn what this tick's driving costs, and stop when the tank is empty.
  //
  // Only players are held to it. A simulated county running out of fuel would
  // silt up with parked cars and stop being traffic, and the traffic is the
  // noise the whole game runs on.
  if (vehicle.kind === 'player') {
    // Gallons burned this tick: meters traveled, into miles, over the mpg.
    const gallons = (vehicle.speed * dt) / METERS_PER_MILE / FUEL_MPG;
    vehicle.fuel = Math.max(0, vehicle.fuel - gallons);
    if (vehicle.fuel <= 0) {
      vehicle.speed = 0;
      updatePosition(world, vehicle);
      return;
    }
  }

  let remaining = vehicle.speed * dt;

  // A vehicle can cross more than one node in a tick on short town blocks, so
  // this consumes distance in a loop. The guard bounds a pathological case
  // (a zero-length edge should be impossible: loadCounty rejects them).
  for (let guard = 0; guard < 64 && remaining > 0; guard++) {
    const edgeLength = county.edgeLen[vehicle.edge] as number;
    const left = edgeLength - vehicle.offset;

    if (remaining < left) {
      vehicle.offset += remaining;
      remaining = 0;
      break;
    }

    remaining -= left;
    vehicle.offset = edgeLength;
    if (!crossNode(world, vehicle)) break;
  }

  updatePosition(world, vehicle);
}

/**
 * Handle arrival at the far node of the current edge. Returns true when the
 * vehicle continued onto another edge, false when the trip ended here.
 */
function crossNode(world: World, vehicle: Vehicle): boolean {
  const county = world.county;
  const arrivedEdge = vehicle.edge;
  const node = vehicle.forward
    ? (county.edgeB[arrivedEdge] as number)
    : (county.edgeA[arrivedEdge] as number);

  vehicle.routeIndex++;
  const nextEdge = vehicle.route[vehicle.routeIndex] ?? -1;

  emitTraversal(world, {
    vehicle,
    node,
    fromEdge: arrivedEdge,
    toEdge: nextEdge,
    time: world.time,
  });

  if (nextEdge < 0 && (vehicle.manual || vehicle.quarry !== undefined)) {
    // Being driven, or in pursuit. The route is extended one junction at a time
    // rather than ending: a manual driver's steering decides the exit, and a
    // pursuit unit carries straight on (no `want`) so it never parks mid-chase
    // -- tickPursuits re-aims it toward the quarry a moment later.
    const chosen = pickExit(world, vehicle, node, arrivedEdge, vehicle.want);
    if (chosen >= 0) {
      vehicle.route = [...vehicle.route.slice(0, vehicle.routeIndex), chosen];
      vehicle.routeIndex = vehicle.route.length - 1;
      enterEdge(world, vehicle, chosen, node);
      return true;
    }
  }

  if (nextEdge < 0) {
    // Arrived. Park at the node and pick a new destination after a dwell.
    //
    // The road it came in on is kept, so it can be drawn at the kerb just short
    // of the junction rather than standing in the middle of it. See
    // Vehicle.parkedEdge.
    vehicle.parkedEdge = arrivedEdge;
    vehicle.edge = -1;
    vehicle.atNode = node;
    vehicle.offset = 0;
    vehicle.speed = 0;
    vehicle.route = [];
    vehicle.routeIndex = 0;
    // How long they stay is a property of where they went, not a random wait.
    const habit = vehicle.habits.find((h) => h.node === node);
    vehicle.dwellUntil =
      world.time +
      (habit
        ? habit.dwell * world.rng.range(0.7, 1.3)
        : world.rng.range(DWELL_SECONDS_MIN, DWELL_SECONDS_MAX));
    return false;
  }

  enterEdge(world, vehicle, nextEdge, node);
  return true;
}

/**
 * Which road to take out of a junction.
 *
 * The driver names a **compass direction** and this finds the road that leaves
 * closest to it. That is the whole of the road-following: nobody steers along
 * an edge, because there is nothing to steer -- the car is on a road and the
 * road goes where it goes. All a driver decides is which one to be on next.
 *
 * With no direction asked for, it carries straight on: the road that turns
 * least from the way the car arrived, which is what a road *continuing* through
 * a crossroads looks like.
 *
 * A U-turn is only taken when it is the only way out, which is what a dead end
 * is. Otherwise asking for south while heading north at a crossroads would give
 * back the road just traveled, and a car that reverses down its own path every
 * time somebody fat-fingers a key is not driving.
 */
function pickExit(
  world: World,
  vehicle: Vehicle,
  node: number,
  fromEdge: number,
  want: number | undefined,
): number {
  const county = world.county;
  const came = travelAngle(county, fromEdge, vehicle.forward);

  let best = -1;
  let bestScore = Number.POSITIVE_INFINITY;
  let fallback = -1;

  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;
  for (let i = start; i < end; i++) {
    const edge = county.adjEdge[i] as number;
    if (!edgeEnterableFrom(county, edge, node)) continue;

    if (edge === fromEdge) {
      fallback = edge;
      continue;
    }

    const leavesForward = (county.edgeA[edge] as number) === node;
    const goes = travelAngle(county, edge, leavesForward);
    // How far this road is from what was asked for, or from straight on when
    // nothing was. Wrapped to [-pi, pi] and taken as an absolute.
    const against = want === undefined ? came : want;
    const score = Math.abs(Math.atan2(Math.sin(goes - against), Math.cos(goes - against)));

    if (score < bestScore) {
      bestScore = score;
      best = edge;
    }
  }

  return best >= 0 ? best : fallback;
}

/** The compass angle a car travels at along an edge, in radians. */
function travelAngle(county: County, edge: number, forward: boolean): number {
  const a = forward ? (county.edgeA[edge] as number) : (county.edgeB[edge] as number);
  const b = forward ? (county.edgeB[edge] as number) : (county.edgeA[edge] as number);
  return Math.atan2(
    (county.nodeY[b] as number) - (county.nodeY[a] as number),
    (county.nodeX[b] as number) - (county.nodeX[a] as number),
  );
}

function enterEdge(world: World, vehicle: Vehicle, edge: number, fromNode: number): void {
  const county = world.county;
  const cameFrom = vehicle.edge;
  vehicle.edge = edge;
  vehicle.atNode = -1;
  vehicle.forward = (county.edgeA[edge] as number) === fromNode;
  vehicle.offset = 0;
  vehicle.parkedEdge = -1;

  /**
   * An overtake ends at the junction.
   *
   * A driver who is still out at the line pulls in to take the turn, and every
   * car entering a road starts in the kerb lane whatever it was doing on the
   * last one. This is also what keeps the maneuver bounded to a single edge,
   * which is the difference between a rule with an end and a car that decided
   * to drive on the wrong side of the county.
   */
  vehicle.lane = 0;
  delete vehicle.passUntil;

  // Out of the corner at whatever the corner allowed, not at the posted limit.
  // The car then accelerates back up along the new road; see advanceVehicle.
  const limit = (county.edgeSpeed[edge] as number) * vehicle.speedMultiplier;
  const corner = cameFrom >= 0 ? cornerSpeed(county, cameFrom, edge, fromNode) : limit;
  vehicle.speed = Math.min(limit, Math.max(vehicle.speed, MIN_CORNER_SPEED), corner);
}

/**
 * Compass bearing of an edge, leaving `fromNode`.
 *
 * Straight-line rather than following the geometry, which is what the whole
 * road graph does: an edge between two junctions is one segment as far as the
 * simulation is concerned. See DESIGN.md section 5.
 */
function bearingOf(county: County, edge: number, fromNode: number): number {
  const a = county.edgeA[edge] as number;
  const b = county.edgeB[edge] as number;
  const other = a === fromNode ? b : a;
  const dx = (county.nodeX[other] as number) - (county.nodeX[fromNode] as number);
  const dy = (county.nodeY[other] as number) - (county.nodeY[fromNode] as number);
  return Math.atan2(dx, dy);
}

/**
 * The fastest a car can take this turn.
 *
 * From the turn angle and a comfortable lateral acceleration. A straight-on
 * junction costs nothing; a right angle brings a car down to something like
 * twenty miles an hour; a hairpin to the floor.
 *
 * The corner radius is inferred from the angle rather than measured, because
 * the graph has no junction geometry to measure -- an edge is a straight line
 * between two nodes. What matters is that sharper is slower and that the
 * relationship is the physical one.
 */
export function cornerSpeed(county: County, fromEdge: number, toEdge: number, node: number): number {
  // Bearing the car arrives on, and the one it leaves on.
  const incoming = bearingOf(county, fromEdge, node) + Math.PI;
  const outgoing = bearingOf(county, toEdge, node);

  let turn = Math.abs(outgoing - incoming);
  while (turn > Math.PI) turn = Math.abs(turn - 2 * Math.PI);

  // Nearly straight: no corner at all.
  if (turn < 0.15) return Number.POSITIVE_INFINITY;

  /**
   * The radius a turn is taken on, which is not a constant.
   *
   * A fixed junction width made a fifteen-degree bend cap traffic at 26 mph,
   * which is wrong in the most visible way possible: highways have bends like
   * that and nobody slows for them. The graph collapses two different things
   * into one -- *turning at a junction*, where you change roads, and *following
   * a curve*, where you do not -- and the difference between them is really the
   * geometry of the road.
   *
   * So the radius scales with the slower of the two roads. Fast roads are built
   * with gentle geometry and slow ones are not, which is the correlation that
   * makes one number stand in for both cases.
   */
  const limitIn = county.edgeSpeed[fromEdge] as number;
  const limitOut = county.edgeSpeed[toEdge] as number;
  const base = JUNCTION_RADIUS_METERS + 0.9 * Math.min(limitIn, limitOut);

  const radius = base / Math.tan(turn / 2);
  const speed = Math.sqrt(LATERAL_ACCEL * radius);

  // Below walking-pace-plus, a turn costs nothing: an ordinary junction taken
  // at twenty miles an hour needs no braking at all.
  if (speed >= FREE_CORNER_SPEED) return speed;
  return Math.max(MIN_CORNER_SPEED, speed);
}

/**
 * How much of a county's traffic is going to a town rather than to a random
 * point on a section road.
 *
 * A rural county's trips are not uniform over its road network: people drive
 * to the places where the jobs, the school and the grocery store are. Without
 * this the towns are as empty as the back roads, which gets the whole texture
 * of the map wrong. It also matters for the game, because it is why a camera
 * on a town approach is worth more than one out on a section line, and that
 * asymmetry is what enforcement has to reason about.
 */
const TOWN_TRIP_WEIGHT_DIVISOR = 8;

function buildDestinationWeights(county: County, reachable: Int32Array): Float64Array {
  const weights = new Float64Array(reachable.length).fill(1);

  for (const place of county.places) {
    // A town's built-up area grows with the square root of its population.
    const sigma = 400 + Math.sqrt(place.population) * 16;
    const twoSigmaSquared = 2 * sigma * sigma;

    const shares = new Float64Array(reachable.length);
    let total = 0;
    for (let i = 0; i < reachable.length; i++) {
      const node = reachable[i] as number;
      const dx = (county.nodeX[node] as number) - place.x;
      const dy = (county.nodeY[node] as number) - place.y;
      const share = Math.exp(-(dx * dx + dy * dy) / twoSigmaSquared);
      shares[i] = share;
      total += share;
    }
    if (total <= 0) continue;

    const budget = place.population / TOWN_TRIP_WEIGHT_DIVISOR;
    for (let i = 0; i < reachable.length; i++) {
      weights[i] = (weights[i] as number) + ((shares[i] as number) / total) * budget;
    }
  }

  const cdf = new Float64Array(reachable.length);
  let running = 0;
  for (let i = 0; i < reachable.length; i++) {
    running += weights[i] as number;
    cdf[i] = running;
  }
  return cdf;
}

/**
 * Send a vehicle to a node. Returns false when there is no route to it.
 *
 * This is the only way a player vehicle ever moves. The client asks for a
 * destination and the server answers with yes or no, which is what makes the
 * server authoritative over movement rather than a relay for positions.
 */
export function setDestination(
  world: World,
  vehicle: Vehicle,
  destination: number,
  /**
   * Edges the traveller would rather not be seen on. Penalized, never banned:
   * see AVOID_EDGE_PENALTY for why a ban would leak coverage the player has not
   * earned.
   */
  avoid?: ReadonlySet<number>,
): boolean {
  const county = world.county;
  if (destination < 0 || destination >= county.nodeCount) return false;

  // Re-route from where the vehicle will next be able to turn, which is the
  // node it is heading toward. Turning around mid-edge is not something a car
  // does, and letting the client ask for it would be a movement exploit.
  const from =
    vehicle.edge < 0
      ? vehicle.atNode
      : vehicle.forward
        ? (county.edgeB[vehicle.edge] as number)
        : (county.edgeA[vehicle.edge] as number);
  if (from < 0) return false;

  if (from === destination) {
    // Already going there. Let it finish the edge it is on and stop.
    vehicle.route = vehicle.edge < 0 ? [] : [vehicle.edge];
    vehicle.routeIndex = vehicle.edge < 0 ? 0 : 0;
    vehicle.destination = destination;
    return true;
  }

  const route = world.router.route(from, destination, avoid);
  if (route === null) return false;

  vehicle.destination = destination;
  if (vehicle.edge < 0) {
    if (route.length === 0) return false;
    vehicle.route = route;
    vehicle.routeIndex = 0;
    enterEdge(world, vehicle, route[0] as number, from);
    return true;
  }

  // Mid-edge: keep the current edge as the head of the new route so the
  // vehicle finishes crossing it before the first turn.
  vehicle.route = [vehicle.edge, ...route];
  vehicle.routeIndex = 0;
  return true;
}

/** Park a vehicle at the next node it reaches. */
export function clearDestination(world: World, vehicle: Vehicle): void {
  vehicle.route = vehicle.edge < 0 ? [] : [vehicle.edge];
  vehicle.routeIndex = 0;
  vehicle.destination = vehicle.edge < 0 ? vehicle.atNode : vehicle.destination;
  vehicle.dwellUntil = world.time + 1e9;
}

/** A destination node drawn against the weights above. */
export function pickDestination(world: World): number {
  const cdf = (world.destinationCdf ??= buildDestinationWeights(world.county, world.reachable));
  const roll = world.rng.next() * (cdf[cdf.length - 1] as number);
  let low = 0;
  let high = cdf.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if ((cdf[mid] as number) < roll) low = mid + 1;
    else high = mid;
  }
  return world.reachable[low] as number;
}

/** Pick a destination and route to it. False when no route was found. */
function startTrip(world: World, vehicle: Vehicle): boolean {
  const destination = nextDestination(world, vehicle);
  if (destination < 0) return false;
  return beginTripTo(world, vehicle, destination);
}

/** Route a parked vehicle to a node and set it off. False when unreachable. */
function beginTripTo(world: World, vehicle: Vehicle, destination: number): boolean {
  const from = vehicle.atNode;
  if (from < 0 || destination < 0 || destination === from) return false;

  const route = world.router.route(from, destination);
  if (route === null || route.length === 0) return false;

  vehicle.route = route;
  vehicle.routeIndex = 0;
  vehicle.destination = destination;
  enterEdge(world, vehicle, route[0] as number, from);
  return true;
}

/**
 * Lateral offset from the centerline in meters, so opposing traffic does not
 * draw on top of itself. Right-hand traffic.
 */
const LANE_OFFSET = 2.2;

/**
 * How far short of the junction a parked car stands, in meters.
 *
 * About two car lengths. Far enough that it reads as parked rather than as
 * stopped at the line, close enough that it is obviously at the place it drove
 * to.
 */
const PARK_SETBACK = 10;

/** Any road meeting this node, for standing a car beside rather than on. */
function anyRoadAt(county: County, node: number): number {
  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;
  return end > start ? (county.adjEdge[start] as number) : -1;
}

/**
 * How far right of the centerline this vehicle sits, in meters.
 *
 * Lane 0 is the kerb lane and every lane after it is one lane width further
 * out. Lane -1 is the oncoming lane and is placed exactly where oncoming lane 0
 * sits -- head on, not merely leftish -- because that is what the maneuver is
 * and the map should not soften it.
 */
function lateralOffset(lane: number): number {
  return lane < 0 ? -LANE_OFFSET : LANE_OFFSET + lane * LANE_WIDTH;
}

function updatePosition(world: World, vehicle: Vehicle): void {
  const county = world.county;

  if (vehicle.edge < 0) {
    const node = vehicle.atNode;
    const nx = county.nodeX[node] as number;
    const ny = county.nodeY[node] as number;

    /**
     * Parked at the kerb, not in the crossroads.
     *
     * Every node in this county is a junction, so a car drawn at its node is a
     * car stopped dead in an intersection -- and with two thousand of them
     * parked at any moment, that is what the map looked like. It is placed back
     * along the road it arrived on instead, pulled over to the right, which is
     * where a car that has arrived somewhere is.
     *
     * The graph position is still the node. Nothing downstream reads this.
     */
    const road = vehicle.parkedEdge;
    if (road < 0) {
      vehicle.x = nx;
      vehicle.y = ny;
      return;
    }

    const from = otherNode(county, road, node);
    const dx = nx - (county.nodeX[from] as number);
    const dy = ny - (county.nodeY[from] as number);
    const span = Math.hypot(dx, dy);
    if (span <= 0) {
      vehicle.x = nx;
      vehicle.y = ny;
      return;
    }

    // Never past the far end of a short road, so a car on a twenty-meter block
    // does not park in the junction behind it.
    const back = Math.min(PARK_SETBACK, span * 0.5);
    const inverse = 1 / span;
    vehicle.x = nx - dx * inverse * back + dy * inverse * LANE_OFFSET;
    vehicle.y = ny - dy * inverse * back - dx * inverse * LANE_OFFSET;
    vehicle.heading = Math.atan2(dy, dx);
    return;
  }

  const edge = vehicle.edge;
  const from = vehicle.forward ? (county.edgeA[edge] as number) : (county.edgeB[edge] as number);
  const to = vehicle.forward ? (county.edgeB[edge] as number) : (county.edgeA[edge] as number);

  const ax = county.nodeX[from] as number;
  const ay = county.nodeY[from] as number;
  const bx = county.nodeX[to] as number;
  const by = county.nodeY[to] as number;

  const length = county.edgeLen[edge] as number;
  const t = length > 0 ? Math.min(1, vehicle.offset / length) : 0;

  const dx = bx - ax;
  const dy = by - ay;
  const inverse = 1 / Math.hypot(dx, dy || 1e-9);

  // Perpendicular pointing to the driver's right.
  const rightX = dy * inverse;
  const rightY = -dx * inverse;

  const lateral = lateralOffset(vehicle.lane);
  vehicle.x = ax + dx * t + rightX * lateral;
  vehicle.y = ay + dy * t + rightY * lateral;
  vehicle.heading = Math.atan2(dy, dx);
}

function emitTraversal(world: World, event: NodeTraversal): void {
  for (const listener of world.traversalListeners) listener(event);
}

export function onTraversal(world: World, listener: TraversalListener): () => void {
  world.traversalListeners.push(listener);
  return () => {
    const index = world.traversalListeners.indexOf(listener);
    if (index >= 0) world.traversalListeners.splice(index, 1);
  };
}
